import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import MusicPlayer from './MusicPlayer';
import '../styles/VideoHub.css';

const COMMERCIAL_BASE_URL = 'https://komokshow.netlify.app/clips/commercial';

// Video configuration - videos unlock based on party progression
// Only non-game videos are shown publicly; macro game videos are admin-only
const VIDEOS = [
  {
    id: 'party-video',
    label: 'STOCK Party',
    icon: '🎬',
    codeName: 'KOMOK (III)',
    subtitle: 'Full Party Video',
    vimeoUrl: 'https://player.vimeo.com/video/1186629236?h=852607001b&badge=0&autopause=0&player_id=0&app_id=58479',
    alwaysUnlocked: true,
  },
  {
    id: 'hype1',
    label: 'Hype Vid',
    icon: '🔥',
    codeName: 'HYPE VIDEO I',
    subtitle: 'The Invitation',
    vimeoUrl: 'https://player.vimeo.com/video/1105351638?h=91399453d5&badge=0&autopause=0&player_id=0&app_id=58479',
    alwaysUnlocked: true,
  },
  {
    id: 'squid-game',
    label: 'Squid Game',
    icon: '🦑',
    codeName: 'PARTY I',
    subtitle: 'Squid Game',
    vimeoUrl: 'https://player.vimeo.com/video/1004378850?h=b69d7125c5&badge=0&autopause=0&player_id=0&app_id=58479',
    alwaysUnlocked: true,
  },
  {
    id: 'hogwarts',
    label: 'Harry Potter',
    icon: '🧙',
    codeName: 'PARTY II',
    subtitle: 'The Hogwarts Experience',
    vimeoUrl: 'https://player.vimeo.com/video/1076582009?h=68cc18f98b&badge=0&autopause=0&player_id=0&app_id=58479',
    alwaysUnlocked: true,
  },
  {
    id: 'kook',
    label: 'KOOK Olympics',
    icon: '🏄',
    codeName: 'KOOK Olympics PARTY',
    subtitle: 'KOOK',
    vimeoUrl: 'https://player.vimeo.com/video/1002483563?h=414573fb2f&badge=0&autopause=0&player_id=0&app_id=58479',
    alwaysUnlocked: true,
  },
  {
    id: 'registration',
    label: 'Register',
    icon: '📝',
    codeName: 'III REGISTRATION',
    subtitle: 'Onboarding Process',
    url: '/videos/registration.mp4',
    phaseKey: 'registration',
    group: 'iii-party',
  },
  {
    id: 'orientation',
    label: 'Orientation',
    icon: '🎓',
    codeName: 'III ORIENTATION',
    subtitle: 'Training Protocol',
    url: '/videos/orientation_video.mp4',
    phaseKey: 'orientation',
    group: 'iii-party',
  },
  {
    id: 'golf-ppt',
    label: 'Golf PPT',
    icon: '⛳',
    codeName: 'DEAD CAT BOUNCE',
    subtitle: 'Golf Game Presentation',
    embedUrl: 'https://docs.google.com/presentation/d/1DjxO-Q2uz6XwrGep4fGbqTUciPusJRdnqa3FEkYVdpg/embed?start=false&loop=false&delayms=3000',
    alwaysUnlocked: true,
    group: 'iii-party',
  },
  {
    id: 'briefcase-video',
    label: 'A Brief Case',
    icon: '💰',
    codeName: 'A BRIEF CASE',
    subtitle: 'Macro Game Intro',
    url: '/videos/a_brief_case_video.mp4',
    alwaysUnlocked: true,
    group: 'iii-party',
  },
  {
    id: 'hype2',
    label: 'Hype Video 2 for III',
    icon: '🔥',
    codeName: 'HYPE VIDEO II',
    subtitle: 'The Anticipation',
    vimeoUrl: 'https://player.vimeo.com/video/1065367766?h=d3a6894c79&badge=0&autopause=0&player_id=0&app_id=58479',
    alwaysUnlocked: true,
    group: 'iii-party',
  },
];

const VIDEO_GROUPS = {
  'iii-party': {
    label: 'III Party Videos',
    icon: '🗂️',
  },
};

// Build top-level tabs: ungrouped videos preserve order, grouped videos collapse into a single parent tab.
const TOP_LEVEL_TABS = (() => {
  const tabs = [];
  const seenGroups = new Set();
  for (const v of VIDEOS) {
    if (v.group) {
      if (!seenGroups.has(v.group)) {
        seenGroups.add(v.group);
        const g = VIDEO_GROUPS[v.group];
        const firstChild = VIDEOS.find(x => x.group === v.group);
        tabs.push({
          type: 'group',
          id: v.group,
          label: g.label,
          icon: g.icon,
          firstChildId: firstChild ? firstChild.id : null,
        });
      }
    } else {
      tabs.push({ type: 'video', video: v });
    }
  }
  return tabs;
})();

// Ordered progression of phases
const PHASE_ORDER = [
  'registration',
  'orientation',
  'trading-1',
  'portfolio-warfare',
  'trading-2',
  'dead-cat-bounce',
  'trading-3',
  'a-brief-case',
  'trading-4',
  'liquidity-crisis',
  'closing',
  'winners',
];

// Map party state to phase key
const getPhaseKey = (phase, tradingBlock, macroGame) => {
  if (phase === 'not_started' || phase === 'registration' || phase === 'premarket') {
    return 'registration';
  }
  if (phase === 'orientation') {
    return 'orientation';
  }
  if (phase === 'trading') {
    return `trading-${tradingBlock}`;
  }
  if (phase === 'macro_game') {
    if (macroGame) {
      return macroGame.toLowerCase().replace(/\s+/g, '-');
    }
    const gameByBlock = {
      1: 'portfolio-warfare',
      2: 'dead-cat-bounce',
      3: 'a-brief-case',
      4: 'liquidity-crisis',
    };
    return gameByBlock[tradingBlock] || 'portfolio-warfare';
  }
  if (phase === 'liquidity_crisis') {
    return 'liquidity-crisis';
  }
  if (phase === 'closing') {
    return 'closing';
  }
  if (phase === 'winners' || phase === 'results' || phase === 'ended') {
    return 'winners';
  }
  return 'registration';
};

// Check if a video is unlocked based on current phase and completed games
const isVideoUnlocked = (videoPhaseKey, currentPhaseKey, completedGamesList) => {
  const videoIndex = PHASE_ORDER.indexOf(videoPhaseKey);
  const currentIndex = PHASE_ORDER.indexOf(currentPhaseKey);

  if (videoIndex !== -1 && currentIndex !== -1 && videoIndex <= currentIndex) {
    return true;
  }

  if (completedGamesList && completedGamesList.length > 0) {
    const lastCompletedGame = completedGamesList[completedGamesList.length - 1];
    const lastGameKey = lastCompletedGame.toLowerCase().replace(/\s+/g, '-');
    const lastGameIndex = PHASE_ORDER.indexOf(lastGameKey);
    if (videoIndex !== -1 && lastGameIndex !== -1 && videoIndex <= lastGameIndex) {
      return true;
    }
  }

  if (currentPhaseKey !== 'registration' && videoPhaseKey === 'registration') {
    return true;
  }

  return false;
};

const VideoHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section') || 'videos';
  const tabParam = searchParams.get('tab') || 'party-video';
  const commercialParam = searchParams.get('commercial') || null;

  const activeSection = sectionParam === 'commercials' ? 'commercials' : sectionParam === 'music' ? 'music' : 'videos';
  const [activeTab, setActiveTabState] = useState(tabParam);
  const [partyPhase, setPartyPhase] = useState('not_started');
  const [tradingBlock, setTradingBlock] = useState(1);
  const [currentMacroGame, setCurrentMacroGame] = useState(null);
  const [completedGames, setCompletedGames] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [commercialError, setCommercialError] = useState(false);
  const navigate = useNavigate();

  // Sync tab from URL param
  useEffect(() => {
    if (tabParam && VIDEOS.some(v => v.id === tabParam)) {
      setActiveTabState(tabParam);
    }
  }, [tabParam]);

  // Resolve commercial from URL param once stocks load
  useEffect(() => {
    if (commercialParam && stocks.length > 0 && !selectedCommercial) {
      const match = stocks.find(s => s.ticker.toLowerCase() === commercialParam.toLowerCase());
      if (match) {
        setSelectedCommercial(match);
        setCommercialError(false);
      }
    }
  }, [commercialParam, stocks, selectedCommercial]);

  const setActiveSection = (section) => {
    const params = new URLSearchParams(searchParams);
    params.set('section', section);
    params.delete('tab');
    params.delete('commercial');
    setSearchParams(params, { replace: true });
    setSelectedCommercial(null);
    setCommercialError(false);
  };

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    params.delete('commercial');
    setSearchParams(params, { replace: true });
  };

  const selectCommercial = (stock) => {
    setSelectedCommercial(stock);
    setCommercialError(false);
    const params = new URLSearchParams(searchParams);
    params.set('commercial', stock.ticker);
    setSearchParams(params, { replace: true });
  };

  const clearCommercial = () => {
    setSelectedCommercial(null);
    setCommercialError(false);
    const params = new URLSearchParams(searchParams);
    params.delete('commercial');
    setSearchParams(params, { replace: true });
  };

  // Fetch party state to determine which videos are unlocked
  useEffect(() => {
    const fetchState = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/party-state`);
        if (res.ok) {
          const data = await res.json();
          if (data.partyState) {
            setPartyPhase(data.partyState.phase || 'not_started');
            setTradingBlock(data.partyState.tradingBlockNumber || 1);
            setCurrentMacroGame(data.partyState.currentMacroGame || null);
            setCompletedGames(data.partyState.completedGames || []);
          }
        }
      } catch (error) {
        console.error('[VideoHub] Error fetching party state:', error);
      }
    };

    fetchState();
    const interval = setInterval(fetchState, 10000);
    return () => clearInterval(interval);
  }, []);

  // Fetch stock tickers for commercials tab
  useEffect(() => {
    if (activeSection !== 'commercials' || stocks.length > 0) return;
    const fetchStocks = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/stocks`);
        if (res.ok) {
          const data = await res.json();
          setStocks(data.map(s => ({ ticker: s.ticker, name: s.name })));
        }
      } catch (e) {
        console.error('[VideoHub] Error fetching stocks:', e);
      }
    };
    fetchStocks();
  }, [activeSection, stocks.length]);

  const currentPhaseKey = getPhaseKey(partyPhase, tradingBlock, currentMacroGame);
  const activeVideo = VIDEOS.find(v => v.id === activeTab) || VIDEOS[0];
  const isUnlocked = activeVideo.alwaysUnlocked || isVideoUnlocked(activeVideo.phaseKey, currentPhaseKey, completedGames);

  const renderVideoContent = () => {
    if (!isUnlocked || (!activeVideo.url && !activeVideo.vimeoUrl && !activeVideo.embedUrl)) {
      return (
        <div className="video-coming-soon">
          <div className="coming-soon-icon">
            <div className="icon-static"></div>
          </div>
          <p className="coming-soon-text">COMING SOON</p>
          <p className="coming-soon-sub">THIS FEED IS NOT YET AVAILABLE</p>
          <div className="coming-soon-details">
            <span className="detail-label">STATUS:</span>
            <span className="detail-value">PENDING TRANSMISSION</span>
          </div>
        </div>
      );
    }

    if (activeVideo.vimeoUrl) {
      return (
        <div className="vimeo-player-container">
          <iframe
            src={activeVideo.vimeoUrl}
            className="vimeo-player"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={activeVideo.codeName}
            key={activeVideo.id}
          />
        </div>
      );
    }

    if (activeVideo.embedUrl) {
      return (
        <div className="vimeo-player-container">
          <iframe
            src={activeVideo.embedUrl}
            className="vimeo-player"
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
            title={activeVideo.codeName}
            key={activeVideo.id}
          />
        </div>
      );
    }

    return (
      <div className="video-player-container">
        <video
          className="video-player"
          controls
          controlsList="nodownload"
          playsInline
          key={activeVideo.id}
        >
          <source src={activeVideo.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  };

  const renderCommercialsContent = () => {
    if (selectedCommercial) {
      const commercialUrl = `${COMMERCIAL_BASE_URL}/${selectedCommercial.ticker.toLowerCase()}_commercial.mp4`;
      const currentIndex = stocks.findIndex(s => s.ticker === selectedCommercial.ticker);
      const total = stocks.length;
      const prevStock = total > 0 ? stocks[(currentIndex - 1 + total) % total] : null;
      const nextStock = total > 0 ? stocks[(currentIndex + 1) % total] : null;

      const goPrev = () => prevStock && selectCommercial(prevStock);
      const goNext = () => nextStock && selectCommercial(nextStock);

      return (
        <>
          <div className="video-info">
            <div className="video-code-name">{selectedCommercial.ticker}</div>
            <div className="video-subtitle">
              {selectedCommercial.name}
              {total > 0 && (
                <span className="video-counter"> &nbsp;·&nbsp; {String(currentIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
              )}
            </div>
          </div>

          <div className="video-frame">
            <div className="frame-corner top-left"></div>
            <div className="frame-corner top-right"></div>
            <div className="frame-corner bottom-left"></div>
            <div className="frame-corner bottom-right"></div>

            {commercialError ? (
              <div className="video-coming-soon">
                <p className="coming-soon-text">NO COMMERCIAL</p>
                <p className="coming-soon-sub">THIS COMPANY HAS NOT PURCHASED AIRTIME</p>
              </div>
            ) : (
              <div className="video-player-container">
                <video
                  className="video-player"
                  controls
                  autoPlay
                  controlsList="nodownload"
                  playsInline
                  key={selectedCommercial.ticker}
                  onError={() => setCommercialError(true)}
                  onEnded={goNext}
                >
                  <source src={commercialUrl} type="video/mp4" />
                </video>
              </div>
            )}
          </div>

          {nextStock && (
            <div className="commercial-up-next">
              <span className="up-next-label">▶ UP NEXT</span>
              <span className="up-next-ticker">{nextStock.ticker}</span>
              <span className="up-next-name">{nextStock.name}</span>
            </div>
          )}

          <div className="commercial-nav-row">
            <button
              className="commercial-arrow-btn"
              onClick={goPrev}
              title={prevStock ? `Previous: ${prevStock.ticker}` : ''}
            >
              &#9664;&#9664; {prevStock ? prevStock.ticker : 'PREV'}
            </button>
            <button
              className="commercial-back-btn"
              onClick={clearCommercial}
            >
              ▦ ALL
            </button>
            <button
              className="commercial-arrow-btn"
              onClick={goNext}
              title={nextStock ? `Next: ${nextStock.ticker}` : ''}
            >
              {nextStock ? nextStock.ticker : 'NEXT'} &#9654;&#9654;
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="video-info">
          <div className="video-code-name">COMMERCIALS</div>
          <div className="video-subtitle">SPONSORED CONTENT</div>
        </div>

        <div className="commercials-grid">
          {stocks.map(stock => (
            <button
              key={stock.ticker}
              className="commercial-tile"
              onClick={() => selectCommercial(stock)}
            >
              <span className="commercial-ticker">{stock.ticker}</span>
              <span className="commercial-name">{stock.name}</span>
            </button>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={`video-hub ${activeSection === 'music' ? 'video-hub-music' : ''}`}>
      <div className="scanlines"></div>

      <header className="video-hub-header">
        <h1 className="video-hub-title">VIDEO ARCHIVE</h1>
        <p className="video-hub-subtitle">CLASSIFIED RECORDINGS</p>
      </header>

      <div className="video-hub-nav">
        <button
          className={`nav-tab ${activeSection === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveSection('videos')}
        >
          <span className="nav-icon">🎬</span>
          <span className="nav-label">Videos</span>
        </button>
        <button
          className={`nav-tab ${activeSection === 'commercials' ? 'active' : ''}`}
          onClick={() => setActiveSection('commercials')}
        >
          <span className="nav-icon">📺</span>
          <span className="nav-label">Commercials</span>
        </button>
        <button
          className={`nav-tab ${activeSection === 'music' ? 'active' : ''}`}
          onClick={() => setActiveSection('music')}
        >
          <span className="nav-icon">🎵</span>
          <span className="nav-label">Music</span>
        </button>
        <button className="nav-tab" onClick={() => navigate('/broadcast')}>
          <span className="nav-icon">📡</span>
          <span className="nav-label">Broadcast</span>
        </button>
      </div>

      {activeSection === 'videos' && (
        <>
          <div className="video-hub-tabs">
            {TOP_LEVEL_TABS.map(tab => {
              if (tab.type === 'group') {
                const isActive = activeVideo.group === tab.id;
                return (
                  <button
                    key={`group-${tab.id}`}
                    className={`video-tab ${isActive ? 'active' : ''} available`}
                    onClick={() => tab.firstChildId && setActiveTab(tab.firstChildId)}
                  >
                    <span className="tab-icon">{tab.icon}</span>
                    <span className="tab-label">{tab.label}</span>
                  </button>
                );
              }
              const video = tab.video;
              const unlocked = video.alwaysUnlocked || isVideoUnlocked(video.phaseKey, currentPhaseKey, completedGames);
              return (
                <button
                  key={video.id}
                  className={`video-tab ${activeTab === video.id ? 'active' : ''} ${unlocked ? 'available' : ''}`}
                  onClick={() => setActiveTab(video.id)}
                >
                  <span className="tab-icon">{video.icon}</span>
                  <span className="tab-label">{video.label}</span>
                </button>
              );
            })}
          </div>

          {activeVideo.group && (
            <div className="video-hub-tabs video-hub-subtabs">
              {VIDEOS.filter(v => v.group === activeVideo.group).map(video => {
                const unlocked = video.alwaysUnlocked || isVideoUnlocked(video.phaseKey, currentPhaseKey, completedGames);
                return (
                  <button
                    key={video.id}
                    className={`video-tab ${activeTab === video.id ? 'active' : ''} ${unlocked ? 'available' : ''}`}
                    onClick={() => setActiveTab(video.id)}
                  >
                    <span className="tab-icon">{video.icon}</span>
                    <span className="tab-label">{video.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <main className="video-hub-main">
            <div className="video-info">
              <div className="video-code-name">{activeVideo.codeName}</div>
              <div className="video-subtitle">{activeVideo.subtitle}</div>
            </div>

            <div className="video-frame">
              <div className="frame-corner top-left"></div>
              <div className="frame-corner top-right"></div>
              <div className="frame-corner bottom-left"></div>
              <div className="frame-corner bottom-right"></div>

              {renderVideoContent()}
            </div>
          </main>
        </>
      )}

      {activeSection === 'commercials' && (
        <main className="video-hub-main">
          {renderCommercialsContent()}
        </main>
      )}

      {activeSection === 'music' && (
        <main className="video-hub-main">
          <MusicPlayer />
        </main>
      )}

      {activeSection !== 'music' && (
        <footer className="video-hub-footer">
          <div className="footer-stat">
            <span className="stat-label">FEED</span>
            <span className="stat-value">
              {activeSection === 'commercials'
                ? (selectedCommercial ? selectedCommercial.ticker : 'COMMERCIALS')
                : activeVideo.codeName}
            </span>
          </div>
          <div className="footer-stat">
            <span className="stat-label">STATUS</span>
            <span className={`stat-value ${activeSection === 'commercials' || isUnlocked ? 'active' : 'inactive'}`}>
              {activeSection === 'commercials' || isUnlocked ? 'AVAILABLE' : 'LOCKED'}
            </span>
          </div>
          <button className="faq-link" onClick={() => navigate('/faq')}>
            <span className="faq-icon">?</span>
            <span className="faq-text">FAQ</span>
          </button>
        </footer>
      )}
    </div>
  );
};

export default VideoHub;
